import { ConfirmationResult, ListPaging, setList, setListLoaded, setListLoading, updateEachListItem } from '@aston/foundation/signals';
import { IConfirmModalContent, IConfirmModalResult, IListState } from '@aston/foundation/models';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Loader, SortedFilteredList } from '@aston/foundation/classes';
import { toObservable } from '@angular/core/rxjs-interop';
import { emptyList } from '@aston/foundation/functions';
import { firstValueFrom, map, take } from 'rxjs';
import { inject, Signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { ImportDocumentsCompletedSynthesisReportComponent } from '../../components/import-documents-completed-synthesis-report/import-documents-completed-synthesis-report.component';
import { CompletedAccountingListSortProp, ImportReportErrorColumnsListSortProp, ImportReportErrorLinesListSortProp, ImportType } from '../../enums';
import { ICompletedAccountingListItem, IImportErrorColumnRowModel, IImportFileReportErrorLinesList, IImportItem } from '../../models';
import { ImportSynthesisReportComponent } from '../../components/import-synthesis-report/import-synthesis-report.component';
import * as AppStoreActions from '../../../root-store/app-store/actions';
import { AppConstants } from '../../../app.constants';
import { ImportService } from '../../services';

export function withImportDetailReports<_>(REPORT_ERROR_COLUMNS, REPORT_ERROR_LINES) {
	return signalStoreFeature(
		{
			state: type<{
				importItem: IImportItem,
				reportErrorLinesPaging: ListPaging<ImportReportErrorLinesListSortProp>,
			}>(),
			methods: type<{
				reportErrorColumnsSetPaging: (paging: ListPaging<ImportReportErrorColumnsListSortProp>) => Promise<void>,
				reportErrorLinesSetPaging: (paging: ListPaging<ImportReportErrorLinesListSortProp>) => Promise<void>,
				reportErrorColumnsLoader: () => Loader,
				completedAccountingLoader: () => Loader,
				resetSynthesisModalRef: () => void,
				loadReportErrorColumns: () => Promise<void>,
				dismissAll: () => void,
				openModal: (content: any, options?: NgbModalOptions) => NgbModalRef,
				confirm: (content: IConfirmModalContent, answer: (result: IConfirmModalResult) => void) => ConfirmationResult

			}>(),
			props: type<{
				currentType: Signal<ImportType>,
				reportErrorColumnsSortedList: Signal<SortedFilteredList<IImportErrorColumnRowModel, ImportReportErrorColumnsListSortProp>>,
				reportErrorColumnsLoading: Signal<boolean>,
				reportErrorColumnsError: Signal<string | null>,
				completedAccountingSortedList: Signal<SortedFilteredList<ICompletedAccountingListItem, CompletedAccountingListSortProp>>,
			}>(),
		},

		withMethods(store => {
			const service = inject(ImportService);
			const fatStore = inject(Store);

			const loadReportErrorLines = (column: IImportErrorColumnRowModel) => {
				const { filters, ...paging } = store.reportErrorLinesPaging();

				patchState(store, setListLoading(REPORT_ERROR_LINES))

				return firstValueFrom(service.getReportErrorLinesByColumn(store.importItem().id, column, { ...paging, filters }))
					.then(result => {
						patchState(store, setListLoaded(REPORT_ERROR_LINES))
						patchState(store, updateEachListItem(
							(row: IImportErrorColumnRowModel) => ({
								...row,
								reportErrorLinesListPaging: {...store.reportErrorLinesPaging()},
								reportErrorLines: (row.column === column.column)
									? result
									: row.reportErrorLines
							}),
							REPORT_ERROR_COLUMNS))
					})
			}

			const toggleReportErrorLines = (column: IImportErrorColumnRowModel) => patchState(store, updateEachListItem(
				(row: IImportErrorColumnRowModel) => ({
					...row,
					isReportErrorLineListOpened: (row.column === column.column && row.errorCode === column.errorCode)
						? !row.isReportErrorLineListOpened
						: false
				}),
				REPORT_ERROR_COLUMNS))

			const clearReportErrorColumns = () => patchState(store, setList(emptyList<IImportErrorColumnRowModel>(), REPORT_ERROR_COLUMNS))

			const columnsList$ = toObservable(store.reportErrorColumnsSortedList)

			const thisLineProp$ = function<T>(k: keyof IImportErrorColumnRowModel) {
				return c => columnsList$.pipe(
					map(cols => cols.list.items.find(ci => ci.column === c.column )),
					map(row => row[k] as T)
				)
			}

			const reportListActions = () => ({
				UpdateColumnsSettings: props => AppStoreActions.Execute(() => store.reportErrorColumnsSetPaging(props)),
				ToggleLine: ({ column, loadLines }) => AppStoreActions.Execute(() => {
					toggleReportErrorLines(column);
					if (loadLines) loadReportErrorLines(column);
				}),
				LoadLinesRequest: (_, column) => AppStoreActions.Execute(() => loadReportErrorLines(column)), // IS NEVER DISPATCHED ??
				UpdateLinesSettings: ({ column, paging }) => AppStoreActions.Execute(() => {
					store.reportErrorLinesSetPaging(paging)
					loadReportErrorLines(column)
				})
			})

			const reportsSelectors = {
				selectErrorColumnsListIsLoading: toObservable(store.reportErrorColumnsLoading),
				selectErrorColumnsListError: toObservable(store.reportErrorColumnsError),
				selectLinesListIsLoading: thisLineProp$<boolean>('isReportErrorLineListLoading'),
				selectLinesListError: thisLineProp$<string>('reportErrorLinesListError'),
				selectLinesList: thisLineProp$<IImportFileReportErrorLinesList>('reportErrorLines'),
				selectLinesListState: thisLineProp$<IListState<any>>('reportErrorLinesListPaging'),
			}

			const reportListSelectors = () => reportsSelectors;

			const openImportSynthesisReport = () => {
				store.dismissAll();

				const modalRef = store.openModal(ImportSynthesisReportComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, windowClass: 'modal-extended' });
				const modal = (modalRef.componentInstance as ImportSynthesisReportComponent);

				modal.currentImport = store.importItem();
				modal.showDismiss = true;

				modal.actions = { ...reportListActions() };

				modal.list$ = columnsList$;
				modal.listLoader = store.reportErrorColumnsLoader();
				modal.currentImportLoader = Loader.empty();

				modal.selectors = {
					...reportListSelectors(),
					selectLinesListIsLoading: thisLineProp$('isReportErrorLineListLoading'),
					selectLinesListError: thisLineProp$('reportErrorLinesListError'),
					selectLinesList: thisLineProp$('reportErrorLines'),
					selectLinesListState: thisLineProp$('reportErrorLinesListPaging'),
				}

				modal.dismiss.pipe(take(1)).subscribe(_ => {
					clearReportErrorColumns();
					modalRef.dismiss();
					store.resetSynthesisModalRef();
				});

				modal.download.subscribe(_ =>
					fatStore.dispatch(AppStoreActions.DownloadDocument({
						document: store.importItem().report
					})
					));

				return store.loadReportErrorColumns();
			};

			const completedAccountingList$ = toObservable(store.completedAccountingSortedList)

			const openImportDocumentsCompletedSynthesisReport = () => {
				store.dismissAll();

				const modalRef = store.openModal(ImportDocumentsCompletedSynthesisReportComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, windowClass: 'modal-extended' });
				const modal = (modalRef.componentInstance as ImportDocumentsCompletedSynthesisReportComponent);

				modal.currentImport = store.importItem();
				modal.showDismiss = true;

				modal.completedAccountingList$ = completedAccountingList$;
				modal.completedAccountingListLoader = store.completedAccountingLoader();
				modal.currentImportLoader = Loader.empty();

				modal.dismiss.pipe(take(1)).subscribe(_ => modalRef.dismiss());

				modal.dismiss.pipe(take(1)).subscribe(_ => {
					clearReportErrorColumns();
					modalRef.dismiss();
					store.resetSynthesisModalRef();
				});

				modal.download.subscribe(__ =>
					fatStore.dispatch(AppStoreActions.DownloadDocument({ document: store.importItem().synthesis.accountingDocumentsCompletedDocument.documentFiles[0], name: store.importItem().synthesis.accountingDocumentsCompletedDocument.documentFiles[0].fileName })));
			}

			return {
				reportListActions,
				reportListSelectors,
				openImportSynthesisReport,
				openImportDocumentsCompletedSynthesisReport,
			}
		}),
	)
}
